import React, { useState } from 'react';
import { Box, Menu, MenuItem, IconButton, Tooltip } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { SearchBar } from '../SearchBar/SearchBar';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { CustomButton } from '../Infra/CustomButton/CustomButton';
import ConditionalTooltip from './ConditionalTooltip';

interface ToolbarProps {
  onSearch: (term: string) => void;
  placeholder?: string;
  searchTerm?: string;
  disabled?: boolean;
  onExport?: () => void;
  showExportButton?: boolean;
  exportTooltip?: string;
  buttons?: Array<{
    variant: 'text' | 'outlined' | 'contained';
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    disabled?: boolean;
    color?: 'primary' | 'secondary';
    badge?: {
      content: string;
      color?: string;
      sx?: SxProps<Theme>;
    };
    inMenu?: boolean;
    separator?: boolean;
    sx?: SxProps<Theme>;
    tooltip?: string;
  }>;
  menuItems?: Array<{
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    disabled?: boolean;
    badge?: {
      content: string;
      color?: string;
      sx?: SxProps<Theme>;
    };
    separator?: boolean;
    tooltip?: string;
  }>;
}

const Toolbar: React.FC<ToolbarProps> = ({
  onSearch,
  placeholder = 'Search...',
  searchTerm = '',
  disabled = false,
  onExport,
  showExportButton = true,
  exportTooltip,
  buttons = [],
  menuItems = [],
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const regularButtons = buttons.filter(btn => !btn.inMenu);
  const menuButtons = buttons.filter(btn => btn.inMenu);

  // Combine menuItems from prop with buttons marked for menu
  const allMenuItems = [
    ...menuButtons,
    ...menuItems.map(item => ({
      ...item,
      variant: 'text' as const,
      inMenu: true,
    })),
  ];

  // Add export button if enabled and onExport is provided
  const exportButton =
    showExportButton && onExport
      ? {
          variant: 'text' as const,
          label: 'Export',
          onClick: onExport,
          icon: <FileDownloadIcon />,
          disabled: disabled,
          inMenu: true,
          tooltip: exportTooltip || 'Download data as Excel file',
        }
      : null;

  return (
    <Box
      data-testid="toolbar-container"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        opacity: disabled ? 0.8 : 1,
      }}
    >
      <Box sx={{ width: 300 }}>
        <SearchBar
          onSearch={onSearch}
          placeholder={placeholder}
          value={searchTerm}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {regularButtons.map((button, index) => {
          // Generate test-id from button label
          const testId = `toolbar-btn-${button.label.toLowerCase().replace(/\s+/g, '-')}`;
          return (
            <ConditionalTooltip key={index} tooltip={button.tooltip}>
              <CustomButton
                data-testid={testId}
                variant={button.variant}
                onClick={button.onClick}
                startIcon={button.icon}
                disabled={disabled || button.disabled}
                badge={button.badge}
                sx={button.sx}
                color={button.color}
              >
                {button.label}
              </CustomButton>
            </ConditionalTooltip>
          );

          return button.tooltip ? (
            <Tooltip key={index} title={button.tooltip}>
              <span>{buttonElement}</span>
            </Tooltip>
          ) : (
            buttonElement
          );
        })}

        {(allMenuItems.length > 0 || exportButton) && (
          <>
            <Tooltip title="More options">
              <IconButton
                data-testid="toolbar-menu-button"
                onClick={handleMenuOpen}
                disabled={disabled}
                aria-label="More options"
                aria-controls={menuOpen ? 'toolbar-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu
              data-testid="toolbar-menu"
              id="toolbar-menu"
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'toolbar-menu-button',
              }}
            >
              {allMenuItems.map((button, index) => {
                const testId = `toolbar-menu-item-${button.label.toLowerCase().replace(/\s+/g, '-')}`;
                return (
                  <ConditionalTooltip
                    key={index}
                    tooltip={button.tooltip}
                    placement="left"
                  >
                    <MenuItem
                      data-testid={testId}
                      onClick={() => {
                        button.onClick();
                        handleMenuClose();
                      }}
                      disabled={disabled || button.disabled}
                      sx={{
                        color:
                          disabled || button.disabled ? '' : 'secondary.main',
                        display: 'flex',
                        alignItems: button.badge ? 'flex-start' : 'center',
                        py: 1,
                        borderBottom: button.separator
                          ? '1px solid #e0e0e0'
                          : 'none',
                        mb: button.separator ? 1 : 0,
                      }}
                    >
                      {button.icon && (
                        <Box
                          sx={{
                            mr: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            mt: button.badge ? 0.5 : 0,
                          }}
                        >
                          {button.icon}
                        </Box>
                      )}

                      {button.badge ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          {button.label}
                          <Box
                            component="span"
                            sx={{
                              fontSize: '0.7rem',
                              color: 'text.secondary',
                              mt: 0.5,
                            }}
                          >
                            {button.badge.content}
                          </Box>
                        </Box>
                      ) : (
                        button.label
                      )}
                    </MenuItem>
                  </ConditionalTooltip>
                );

                return button.tooltip ? (
                  <Tooltip key={index} title={button.tooltip} placement="left">
                    <span>{menuItem}</span>
                  </Tooltip>
                ) : (
                  menuItem
                );
              })}
              {exportButton && (
                <ConditionalTooltip
                  tooltip={exportButton.tooltip}
                  placement="left"
                >
                  <MenuItem
                    key="export"
                    data-testid="toolbar-menu-item-export"
                    onClick={() => {
                      exportButton.onClick();
                      handleMenuClose();
                    }}
                    disabled={disabled || exportButton.disabled}
                    sx={{
                      color: disabled || exportButton.disabled ? '' : '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      py: 1,
                    }}
                  >
                    <Box
                      sx={{
                        mr: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {exportButton.icon}
                    </Box>
                    {exportButton.label}
                  </MenuItem>
                </ConditionalTooltip>
              )}
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Toolbar;
export { FileDownloadIcon };
